import { useMemo, useCallback, useState } from "react";
import { message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ActionReasonModal from "../../../components/ActionReasonModal/ActionReasonModal";
import {
  TITLE_TXT,
  ROLE_MODAL_WIDTHS,
  ENTERPRISE_SUCCESS_MESSAGES,
} from "../../../constants/constants";
import {
  deactivateEnt,
  activateEnt,
  blacklistEnt,
  getEnterpriseView,
} from "../../../redux/slices/enterpriseSlice";
import { getEnterpriseColumns } from "./getEnterpriseColumns";
import ApproveEntForm from "./ApproveEntForm";
import { useNavigate } from "react-router-dom";
import { count } from "../../../redux/slices/dealsSlice";
import NewEntForm from "../Components/NewEntForm";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../../constants/tableColumnDefaults";

const EnterpriseTable = ({ tableData, selectedStatus, onColumnControlChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { commentSubmitError, commentSubmitLoading, loading } = useSelector(
    (state) => state?.enterprise,
  );
  const [modal, setModal] = useState({ type: null, data: null });
  const [approveModal, setApproveModal] = useState({ type: null, data: null });
  const userPrivileges = useMemo(
    () => user?.privilege?.map((p) => p.privilegeCode) || [],
    [user],
  );

  const closeModal = useCallback(
    () => setModal({ type: null, data: null }),
    [],
  );
  const openModal = useCallback((type, enterprise) => {
    setModal({ type, data: enterprise });
  }, []);
  const openApproveModal = useCallback((type, enterprise) => {
    setApproveModal({ type, data: enterprise });
  }, []);
  const [docUploadEnt, setDocUploadEnt] = useState(null);

  const ACTION_MAP = useMemo(
    () => ({
      deactivate: deactivateEnt,
      activate: activateEnt,
      blacklist: blacklistEnt,
    }),
    [],
  );

  const handleReasonSubmit = useCallback(
    async (values) => {
      if (!modal.type || !modal.data?.entId) return;

      const action = ACTION_MAP[modal.type];

      if (!action) return;

      const res = await dispatch(
        action({
          entId: modal.data.entId,
          comments: values.reason,
          modifiedAt: modal.data.modifiedAt,
        }),
      );

      if (res?.payload?.statusCode === 201) {
        message.success(
          ENTERPRISE_SUCCESS_MESSAGES[modal.type](modal.data?.entName),
        );
        dispatch(getEnterpriseView(selectedStatus));
        dispatch(count("ENTERPRISE"));
        closeModal();
      }
    },
    [dispatch, modal, closeModal, selectedStatus, ACTION_MAP],
  );

  const columns = useMemo(
    () =>
      getEnterpriseColumns({
        onView: (id) =>
          navigate(`/enterprise/ent-details/${id}/${selectedStatus}`),
        onApprove: (enterprise) => openApproveModal("approve", enterprise),
        onUploadDocs: (enterprise) => setDocUploadEnt(enterprise),
        onDeactivate: (enterprise) => openModal("deactivate", enterprise),
        onBlacklist: (enterprise) => openModal("blacklist", enterprise),
        onActivate: (enterprise) => openModal("activate", enterprise),
        status: selectedStatus,
        privileges: userPrivileges,
      }),
    [selectedStatus, userPrivileges, navigate, openApproveModal, openModal],
  );

  return (
    <>
      <CustomTable
        rowKey="entId"
        columns={columns}
        dataSource={tableData}
        loading={loading}
        screenKey={buildTableScreenKey("enterprise")}
        defaultColumnKeys={TABLE_COLUMN_DEFAULTS.enterprise}
        onColumnControlChange={onColumnControlChange}
      />

      {docUploadEnt && (
        <CustomModal
          open
          centered
          width={ROLE_MODAL_WIDTHS}
          title="Upload Documents"
          content={
            <NewEntForm
              onClose={() => setDocUploadEnt(null)}
              selectedStatus={selectedStatus}
              initialStep={1}
              existingEntId={docUploadEnt.entId}
            />
          }
        />
      )}

      <ActionReasonModal
        modalType={modal.type}
        onClose={closeModal}
        onSubmit={handleReasonSubmit}
        submitLoading={commentSubmitLoading}
        submitError={commentSubmitError}
      />
      {approveModal.type && (
        <CustomModal
          title={TITLE_TXT.APPROVE_ENT}
          open
          centered
          width={ROLE_MODAL_WIDTHS}
          content={
            <ApproveEntForm
              onClose={() => setApproveModal(false)}
              selectedStatus={selectedStatus}
              entId={approveModal.data.entId}
            />
          }
        />
      )}
    </>
  );
};

export default EnterpriseTable;
